import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, mergeMap, Observable, switchMap } from 'rxjs';

import { EnvironmentService, ErableHttpService, IdUtils, RequestOptions } from '@ecm/consumer';
import { PortfolioService } from '@ecm/portfolio';

import { HomeLanCodeEnum } from '../business/model/homelan/homelan.view-model';
import { EnvEnum } from '../shared/enums/env.enum';
import { ClearCacheService } from './clearCache.service';

@Injectable({
    providedIn: 'root',
})
export class HomeLanService {
    private static readonly DEFAULT_TIMEOUT = 30 * 1000;
    protected static readonly PARTNER_NAME = 'ERB';
    private static readonly SERVICE_NAME = 'homelan';
    protected static readonly FEATURE_NAME: string = 'dashboard';

    private readonly cacheService = inject(ClearCacheService);
    private readonly environmentService = inject(EnvironmentService);
    private readonly http = inject(ErableHttpService);
    private readonly portfolioService = inject(PortfolioService);

    public getExternalId(contractId: string, code: HomeLanCodeEnum = HomeLanCodeEnum.mlb): Observable<string> {
        return this.portfolioService.getServices().pipe(
            map((services) => {
                return (
                    services
                        .find((service) => service.code === code)
                        ?.instances.find((instance) => instance.contractId === contractId)?.externalId ?? ''
                );
            }),
        );
    }

    public fetch(contractId: string, params: string[]): Observable<any> {
        const services = params.length === 4 ? 'all' : params;

        return this.getExternalId(contractId, HomeLanCodeEnum.mlb).pipe(
            switchMap((externalId: string) => {
                const headers = new HttpHeaders().set('cfsID', externalId);

                return this.http.get(
                    `${this.environmentService.instant('API.DASHBOARD.HOMELAN_URL')}?services=${services}`,
                    {
                        headers,
                        force: true,
                        feature: HomeLanService.FEATURE_NAME,
                        functionality: HomeLanService.FEATURE_NAME,
                        partner: HomeLanService.PARTNER_NAME,
                        service: HomeLanService.SERVICE_NAME,
                        timeout: HomeLanService.DEFAULT_TIMEOUT,
                    },
                );
            }),
        );
    }

    public update(
        contractId: string,
        body: { enable: boolean; level?: string },
        options?: RequestOptions,
    ): Observable<any> {
        this.cacheService.clear([{ apiKey: EnvEnum.HOMELAN_URL }]);
        return this.getExternalId(contractId, HomeLanCodeEnum.mlb).pipe(
            mergeMap((externalId: string) => {
                options = HomeLanService.getRequestOptions({
                    headers: new HttpHeaders().set('cfsID', externalId),
                    ...options,
                });

                return this.http.put(`${this.environmentService.instant(EnvEnum.HOMELAN_ECO_MODE_URL)}`, body, options);
            }),
        );
    }

    private static getRequestOptions(options?: RequestOptions): RequestOptions {
        return {
            feature: HomeLanService.FEATURE_NAME,
            functionality: HomeLanService.FEATURE_NAME,
            pageId: IdUtils.generateRandomId(),
            partner: HomeLanService.PARTNER_NAME,
            service: HomeLanService.SERVICE_NAME,
            timeout: HomeLanService.DEFAULT_TIMEOUT,
            ...(options || {}),
        };
    }
}
